import * as React from 'react';

import type { ExternalInvitationManualValues } from '@/types/ExternalInvitationStep.types';
import ExternalInvitationManualEntryRow from '@/components/organism/CreateSix/steps/StepExternal/ExternalInvitationManualEntryRow';
import ExternalInvitationAddMoreRowsButton from '@/components/organism/CreateSix/steps/StepExternal/ExternalInvitationAddMoreRowsButton';
import { classNames } from '@/utils/classNames.utils';

export interface ExternalInvitationManualRowModel {
  id: string;
  values: ExternalInvitationManualValues;
  emailErrorMessage?: string;
  isAddDisabled?: boolean;
}

interface ExternalInvitationManualPanelProps {
  isActive: boolean;
  tabId: string;
  panelId: string;

  manualRows: ExternalInvitationManualRowModel[];
  onManualRowValuesChange: (rowId: string, nextValues: ExternalInvitationManualValues) => void;
  onAddManualRow: () => void;
  onRemoveManualRow: (rowId: string) => void;
  onAddRowToSix: (rowId: string) => void;
  availableExternalSeats: number;

  className?: string;
}

function ExternalInvitationManualPanel(
  props: ExternalInvitationManualPanelProps
): React.JSX.Element | null {
  const {
    isActive,
    tabId,
    panelId,
    manualRows = [],
    onManualRowValuesChange,
    onAddManualRow,
    onRemoveManualRow,
    onAddRowToSix,
    availableExternalSeats,
    className,
  } = props;

  if (!isActive) return null;

  const hasAvailableSeats = availableExternalSeats > 0;

  return (
    <div
      id={panelId}
      role="tabpanel"
      aria-labelledby={tabId}
      className={classNames('mt-4 space-y-4 overflow-x-hidden', className)}
    >
      {hasAvailableSeats ? (
        <p className="text-sm font-medium">Available External Seats: {availableExternalSeats}</p>
      ) : (
        <p className="text-muted-foreground text-sm">
          No Available External Seats. Please contact your account administrator for assistance
        </p>
      )}

      {hasAvailableSeats && (
        <>
          <div className="space-y-3">
            {manualRows.map((manualRow) => (
              <ExternalInvitationManualEntryRow
                key={manualRow.id}
                values={manualRow.values}
                onValuesChange={(nextValues) => {
                  onManualRowValuesChange(manualRow.id, nextValues);
                }}
                onAdd={() => {
                  onAddRowToSix(manualRow.id);
                }}
                onRemove={() => {
                  onRemoveManualRow(manualRow.id);
                }}
                emailErrorMessage={manualRow.emailErrorMessage}
                isAddDisabled={manualRow.isAddDisabled}
              />
            ))}
          </div>

          <ExternalInvitationAddMoreRowsButton onClick={onAddManualRow} />
        </>
      )}
    </div>
  );
}

ExternalInvitationManualPanel.displayName = 'ExternalInvitationManualPanel';

export default ExternalInvitationManualPanel;
